import React from "react";
import {
  Box,
  Button,
  IconButton,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import { LuChevronDown, LuChevronUp, LuPencil, LuPlus, LuSearch, LuTrash2 } from "react-icons/lu";
import {
  OFF_WHITE,
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  SIDEBAR_BG,
  SIDEBAR_TEXT,
  filterFieldSx,
  PROFILE_FONT,
} from "../../constants/layout";

export const ICON_BLUE = INPUT_BORDER_FOCUS;
export const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
export const BORDER = "#E8E8E8";
export const DETAIL_CARD_BG = "#FAFAFA";

export const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
};

export const headCellSx = {
  fontFamily: PROFILE_FONT,
  fontSize: 12,
  fontWeight: 600,
  color: DARK_NAVY_LIGHT,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  borderBottom: `1px solid ${BORDER}`,
  py: 1.75,
  bgcolor: "#FAFAFA",
  whiteSpace: "nowrap" as const,
};

export const schemeHeadCellSx = {
  ...headCellSx,
  py: 1.25,
  fontSize: 11,
  bgcolor: SIDEBAR_BG,
  color: SIDEBAR_TEXT,
  borderBottom: "1px solid rgba(241, 235, 235, 0.12)",
};

export const bodyCellSx = {
  fontFamily: PROFILE_FONT,
  fontSize: 14,
  color: DARK_NAVY,
  borderBottom: `1px solid #F5F5F5`,
  py: 1.75,
};

export const primaryButtonSx = {
  fontFamily: PROFILE_FONT,
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: 14,
  bgcolor: ICON_BLUE,
  backgroundColor: ICON_BLUE,
  color: "#ffffff",
  borderRadius: "10px",
  px: 2.5,
  py: 1.25,
  boxShadow: "none",
  whiteSpace: "nowrap" as const,
  "&.MuiButton-contained": {
    bgcolor: ICON_BLUE,
    backgroundColor: ICON_BLUE,
    color: "#ffffff",
  },
  "&:hover": { bgcolor: "#16304f", backgroundColor: "#16304f", boxShadow: "none" },
  "&.Mui-disabled": {
    bgcolor: "rgba(30, 58, 95, 0.45)",
    backgroundColor: "rgba(30, 58, 95, 0.45)",
    color: "rgba(255, 255, 255, 0.75)",
  },
};

export const secondaryButtonSx = {
  fontFamily: PROFILE_FONT,
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: 14,
  color: DARK_NAVY_LIGHT,
  borderRadius: "10px",
  px: 2.5,
  py: 1.25,
  border: "1px solid #E0E0E0",
  bgcolor: "#ffffff",
  boxShadow: "none",
  "&:hover": { bgcolor: "#FAFAFA", boxShadow: "none" },
};

export const formatPercent = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "-" || value === "--" || value === "") {
    return "-";
  }
  const num = parseFloat(String(value));
  if (Number.isNaN(num)) return String(value);
  return `${num}%`;
};

export const getReturnColor = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "-" || value === "--" || value === "") {
    return DARK_NAVY_LIGHT;
  }
  const num = parseFloat(String(value));
  if (Number.isNaN(num) || num === 0) return DARK_NAVY_LIGHT;
  return num > 0 ? "#08991F" : "#D32F2F";
};

export const formatListingDate = (value: string | null | undefined) => {
  if (!value || value === "-") return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const ListingAvatar = ({ name }: { name: string }) => {
  const initial = (name?.trim()?.[0] || "?").toUpperCase();
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        bgcolor: DETAIL_CARD_BG,
        border: `1px solid ${BORDER}`,
        color: ICON_BLUE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: PROFILE_FONT,
        fontWeight: 700,
        fontSize: 15,
        flexShrink: 0,
      }}
    >
      {initial}
    </Box>
  );
};

export const CategoryPill = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      px: 1.25,
      py: 0.4,
      borderRadius: "999px",
      fontSize: 12,
      fontWeight: 600,
      fontFamily: PROFILE_FONT,
      bgcolor: "#EEF2F8",
      color: ICON_BLUE,
      whiteSpace: "nowrap",
    }}
  >
    {label || "-"}
  </Box>
);

export const ReturnValue = ({ value }: { value: string | number | null | undefined }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: PROFILE_FONT,
      fontSize: 14,
      fontWeight: 600,
      color: getReturnColor(value),
    }}
  >
    {formatPercent(value)}
  </Typography>
);

export const SortableHeader = ({
  label,
  onSort,
  align = "left",
}: {
  label: string;
  onSort?: () => void;
  align?: "left" | "center" | "right";
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      cursor: onSort ? "pointer" : "default",
      justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
      width: "100%",
    }}
    onClick={onSort}
  >
    {label}
    {onSort ? (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 0 }}>
        <LuChevronUp size={14} />
        <LuChevronDown size={14} style={{ marginTop: -6 }} />
      </Box>
    ) : null}
  </Box>
);

export const ExpandButton = ({ open, onClick }: { open: boolean; onClick: () => void }) => (
  <IconButton
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      color: DARK_NAVY_LIGHT,
      borderRadius: "8px",
      "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
    }}
  >
    {open ? <LuChevronUp size={18} /> : <LuChevronDown size={18} />}
  </IconButton>
);

export const EditAction = ({ onClick }: { onClick: () => void }) => (
  <IconButton
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      color: DARK_NAVY_LIGHT,
      borderRadius: "8px",
      "&:hover": { bgcolor: "#EEF2F8", color: ICON_BLUE },
    }}
  >
    <LuPencil size={16} />
  </IconButton>
);

export const DeleteAction = ({ onClick }: { onClick: () => void }) => (
  <IconButton
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      color: DARK_NAVY_LIGHT,
      borderRadius: "8px",
      "&:hover": { bgcolor: "#FDECEC", color: "#D32F2F" },
    }}
  >
    <LuTrash2 size={16} />
  </IconButton>
);

export const ListingPageShell = ({
  title,
  subtitle,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  action,
  children,
}: {
  title: string;
  subtitle: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      minHeight: "100%",
      bgcolor: OFF_WHITE,
      px: { xs: 3, sm: 5, md: 6, lg: 8 },
      py: { xs: 3, md: 4 },
      fontFamily: PROFILE_FONT,
    }}
  >
    <Box sx={{ maxWidth: 1440, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: { xs: 24, md: 28 },
              fontWeight: 700,
              color: DARK_NAVY,
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
            {subtitle}
          </Typography>
        </Box>
        {action}
      </Box>

      <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
        <TextField
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          variant="outlined"
          fullWidth
          sx={{ ...filterFieldSx, maxWidth: { md: 480 } }}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: "flex", alignItems: "center", pl: 0.5, pr: 1, color: DARK_NAVY_LIGHT }}>
                <LuSearch size={18} />
              </Box>
            ),
          }}
        />
      </Box>

      <Box sx={{ ...cardSx, overflow: "hidden" }}>{children}</Box>
    </Box>
  </Box>
);

export const DetailInfoGrid = ({
  items,
}: {
  items: { label: string; value: React.ReactNode }[];
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
      gap: 2,
      p: 2.5,
      bgcolor: DETAIL_CARD_BG,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
    }}
  >
    {items.map((item) => (
      <Box key={item.label}>
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, fontWeight: 600, color: DARK_NAVY_LIGHT, mb: 0.5 }}>
          {item.label}
        </Typography>
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY, lineHeight: 1.5 }}>
          {item.value ?? "-"}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const PerformanceMiniTable = ({
  title,
  rows,
  columns,
}: {
  title: string;
  rows: { label: string; values: (string | number | null | undefined)[] }[];
  columns: string[];
}) => (
  <Box sx={{ mt: 2 }}>
    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600, color: DARK_NAVY, mb: 1.5 }}>
      {title}
    </Typography>
    <Box
      sx={{
        overflowX: "auto",
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        bgcolor: DETAIL_CARD_BG,
      }}
    >
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: PROFILE_FONT,
          fontSize: 13,
        }}
      >
        <Box component="thead" sx={{ bgcolor: SIDEBAR_BG }}>
          <Box component="tr">
            <Box component="th" sx={{ p: 1.25, textAlign: "left", color: SIDEBAR_TEXT, fontWeight: 600 }}>
              Period
            </Box>
            {columns.map((col) => (
              <Box key={col} component="th" sx={{ p: 1.25, textAlign: "center", color: SIDEBAR_TEXT, fontWeight: 600 }}>
                {col}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row) => (
            <Box component="tr" key={row.label} sx={{ borderTop: `1px solid ${BORDER}` }}>
              <Box component="td" sx={{ p: 1.25, color: DARK_NAVY, fontWeight: 500 }}>
                {row.label}
              </Box>
              {row.values.map((val, idx) => (
                <Box key={idx} component="td" sx={{ p: 1.25, textAlign: "center" }}>
                  <ReturnValue value={val} />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

export const EmptyTableRow = ({ colSpan, message = "No data found" }: { colSpan: number; message?: string }) => (
  <TableCell colSpan={colSpan} sx={{ ...bodyCellSx, textAlign: "center", py: 6, borderBottom: "none" }}>
    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
      {message}
    </Typography>
  </TableCell>
);

export const AddListingButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button onClick={onClick} startIcon={<LuPlus size={18} />} sx={primaryButtonSx}>
    {label}
  </Button>
);
